import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/appHeader.css';
import '../styles/resultsPage.css';
import UserMenu from './UserMenu.jsx';
import FlipEventCard from './FlipEventCard.jsx';
import PlatformsDropdown from './PlatformsDropdown.jsx';
import {
  partitionByMatchKind,
  relatedSectionLabel,
  groupByWatchSurface,
} from '../utils/eventMatchKind.js';
import EntityHub from './entityHub/EntityHub.jsx';
import { fetchEntityHub } from '../api/entityHub';
import { upcomingConcertsFromHub } from '../utils/concertEvents.js';
import { useAuth } from './AuthContext.jsx';
import { goToLogin, goToSignup } from '../utils/guestSearch';

const MOBILE_MQ = '(max-width: 700px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  ));

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

function eventKey(event) {
  return String(event.dbId || event.id || event.title);
}

function resultsSignature(results) {
  return results.map((event) => eventKey(event)).join('|');
}

function ResultsTopic({
  title,
  events,
  youtubeFilter,
  twitchFilter,
  kickFilter,
  epgFilter,
  quiet,
  compact,
  expandedKey,
  onExpand,
  onCollapse,
  staggerOffset = 0,
}) {
  const { liveEPG, upcomingEPG, youtube, twitch, kick } = groupByWatchSurface(events);

  const visibleUpcoming = upcomingEPG.filter(
    (event) => event.platform === 'concert' || epgFilter,
  );
  const showLive = epgFilter && liveEPG.length > 0;
  const showUpcoming = visibleUpcoming.length > 0;
  const showYoutube = youtubeFilter && youtube.length > 0;
  const showTwitch = twitchFilter && twitch.length > 0;
  const showKick = kickFilter && kick.length > 0;
  const showPlatforms = showYoutube || showTwitch || showKick;

  if (!showLive && !showUpcoming && !showPlatforms) {
    return null;
  }

  let stagger = staggerOffset;

  const renderCard = (event, { isLive = false, onBackClick = false, url } = {}) => {
    const key = eventKey(event);
    const index = stagger++;
    return (
      <FlipEventCard
        key={key}
        event={{
          ...event,
          dbId: event.dbId,
          entityId: event.entityId,
          logo: event.logo || event.thumbnail,
          thumbnail: event.thumbnail,
          start: event.start || event.startsAt || event.startedAt,
          end: event.end,
          url: url !== undefined ? url : event.url,
          platform: event.platform,
          viewers: event.viewers,
        }}
        onBackClick={onBackClick}
        isLive={isLive}
        compact={compact}
        expanded={compact && expandedKey === key}
        onExpand={compact ? () => onExpand(key) : undefined}
        onCollapse={compact ? onCollapse : undefined}
        staggerIndex={index}
      />
    );
  };

  return (
    <section className={`results-topic${quiet ? ' results-topic--related' : ''}`}>
      <div className={`topic-header${quiet ? ' topic-header--quiet' : ''}`}>
        <span className="accent-line"></span>
        {title}
      </div>

      {showLive && (
        <div>
          <div className="section-header section-header--nested">
            Live Now
          </div>
          <div
            className={`results-carousel${liveEPG.length === 1 ? ' results-carousel--single' : ''}${compact ? ' results-carousel--compact' : ''}`}
          >
            {liveEPG.map(event => renderCard(event, { isLive: true, onBackClick: true }))}
          </div>
        </div>
      )}

      {showUpcoming && (
        <div>
          <div className="section-header section-header--nested">
            Upcoming
          </div>
          <div className={`results-grid${compact ? ' results-grid--compact' : ''}`}>
            {visibleUpcoming.map(event => renderCard(event, { onBackClick: false }))}
          </div>
        </div>
      )}

      {showPlatforms && (
        <div className="platforms-section">
          <div className="section-header section-header--nested">
            Platforms
          </div>

          {showYoutube && (
            <div className="platform-subsection">
              <div className="platform-header">
                <div className="platform-brand">
                  <svg viewBox="0 0 24 24" fill="none" width="24" height="24" style={{ color: '#FF0000' }}>
                    <path d="M23.498 6.186a2.994 2.994 0 0 0-2.108-2.116C19.228 3.5 12 3.5 12 3.5s-7.228 0-9.39.57A2.994 2.994 0 0 0 .502 6.186C0 8.36 0 12 0 12s0 3.64.502 5.814a2.994 2.994 0 0 0 2.108 2.116C4.772 20.5 12 20.5 12 20.5s7.228 0 9.39-.57a2.994 2.994 0 0 0 2.108-2.116C24 15.64 24 12 24 12s0-3.64-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="currentColor"/>
                  </svg>
                  <h3 className="platform-title">YouTube</h3>
                  <span className="platform-count">{youtube.length} streams</span>
                </div>
              </div>
              <div className={`results-grid${compact ? ' results-grid--compact' : ''}`}>
                {youtube.map(result => renderCard(result, { isLive: true, onBackClick: true }))}
              </div>
            </div>
          )}

          {showTwitch && (
            <div className="platform-subsection">
              <div className="platform-header">
                <div className="platform-brand">
                  <svg viewBox="0 0 24 24" fill="none" width="24" height="24" style={{ color: '#9146FF' }}>
                    <path d="M2.857 0L.286 4.286V21.43h5.143V24h3.429l3.429-2.571h4.286L23.714 16V0H2.857zm17.143 15.429l-2.571 2.571h-4.286l-3.429 2.571v-2.571H3.429V1.714h16.571v13.715z" fill="currentColor"/>
                    <path d="M17.143 6.857h-1.714v5.143h1.714V6.857zm-4.286 0h-1.714v5.143h1.714V6.857z" fill="currentColor"/>
                  </svg>
                  <h3 className="platform-title">Twitch</h3>
                  <span className="platform-count">{twitch.length} streams</span>
                </div>
              </div>
              <div className={`results-grid${compact ? ' results-grid--compact' : ''}`}>
                {twitch.map(result => renderCard(result, { isLive: true, onBackClick: true }))}
              </div>
            </div>
          )}

          {showKick && (
            <div className="platform-subsection">
              <div className="platform-header">
                <div className="platform-brand">
                  <svg viewBox="0 0 24 24" fill="none" width="24" height="24" style={{ color: '#53FC18' }}>
                    <path d="M3 3h5.2v6.2L14.5 3H21l-7.1 7.4L21 21h-6.6l-6.2-7.8V21H3V3z" fill="currentColor"/>
                  </svg>
                  <h3 className="platform-title">Kick</h3>
                  <span className="platform-count">{kick.length} streams</span>
                </div>
              </div>
              <div className={`results-grid${compact ? ' results-grid--compact' : ''}`}>
                {kick.map(result => renderCard(result, { isLive: true, onBackClick: true }))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const GuestBellIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ResultsGallery = ({
  results = [],
  query = '',
  entityId = null,
  onBack,
  viewer = null,
  capabilities = null,
  quotaNotice = false,
}) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const isMobile = useIsMobile();
  const [compact, setCompact] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);
  const [listAnimKey, setListAnimKey] = useState(0);
  const densityLockRef = useRef(false);

  const [youtubeFilter, setYoutubeFilter] = useState(true);
  const [twitchFilter, setTwitchFilter] = useState(true);
  const [kickFilter, setKickFilter] = useState(true);
  const [epgFilter, setEpgFilter] = useState(true);
  const [hub, setHub] = useState(null);
  const [hubReady, setHubReady] = useState(!entityId);

  const useCompact = isMobile && compact;
  const concertEvents = useMemo(() => upcomingConcertsFromHub(hub), [hub]);
  const galleryEvents = useMemo(
    () => [...results, ...concertEvents],
    [results, concertEvents],
  );
  const resultsSig = useMemo(() => resultsSignature(galleryEvents), [galleryEvents]);

  useEffect(() => {
    setListAnimKey((key) => key + 1);
  }, [resultsSig]);

  useEffect(() => {
    let cancelled = false;
    setHub(null);
    setHubReady(!entityId);

    if (!entityId) return undefined;

    (async () => {
      const payload = await fetchEntityHub(entityId);
      if (cancelled) return;
      setHub(payload);
      setHubReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [entityId]);

  const interest = (query || '').trim();
  const { direct, relatedByEntity } = partitionByMatchKind(galleryEvents);
  const visibleEvents = [
    ...direct,
    ...relatedByEntity.flatMap((group) => group.events),
  ];

  const youtubeCount = visibleEvents.filter(result => result.platform === 'youtube').length;
  const twitchCount = visibleEvents.filter(result => result.platform === 'twitch').length;
  const kickCount = visibleEvents.filter(result => result.platform === 'kick').length;
  const epgCount = visibleEvents.filter(result => result.platform === 'EPG').length;

  const noFiltersSelected = !youtubeFilter && !twitchFilter && !kickFilter && !epgFilter;
  const directTitle = interest || 'Results';
  const showGuestChrome = !isLoggedIn
    && viewer !== 'authenticated'
    && capabilities?.canRemind !== true;

  const handleDensity = (nextCompact) => {
    if (nextCompact === compact) return;
    if (densityLockRef.current) return;
    densityLockRef.current = true;

    setCompact(nextCompact);
    setExpandedKey(null);

    // Defer list remount so the density tap is not lost to layout thrash.
    window.requestAnimationFrame(() => {
      setListAnimKey((key) => key + 1);
      window.setTimeout(() => {
        densityLockRef.current = false;
      }, 320);
    });
  };

  const filterProps = {
    youtubeFilter,
    twitchFilter,
    kickFilter,
    epgFilter,
    compact: useCompact,
    expandedKey,
    onExpand: setExpandedKey,
    onCollapse: () => setExpandedKey(null),
  };

  return (
    <div className={`results-page${useCompact ? ' results-page--compact' : ''}${showGuestChrome ? ' results-page--guest-header' : ''}`}>
      <header className="app-header glass">
        <span className="logo-text">LiveNow</span>
        {showGuestChrome ? (
          <div className="guest-notify-strip guest-notify-strip--header">
            <span className="guest-notify-strip-icon" aria-hidden="true">
              <GuestBellIcon />
            </span>
            <p className="guest-notify-strip-copy">
              Don&apos;t miss this going live.{' '}
              <button
                type="button"
                className="guest-notify-strip-link"
                onClick={() => goToLogin(navigate, query)}
              >
                Log in
              </button>
              {' '}or{' '}
              <button
                type="button"
                className="guest-notify-strip-link"
                onClick={() => goToSignup(navigate, query)}
              >
                create an account
              </button>
              {'.'}
            </p>
          </div>
        ) : null}
        <div className="header-right">
          <PlatformsDropdown
            youtubeFilter={youtubeFilter}
            twitchFilter={twitchFilter}
            kickFilter={kickFilter}
            epgFilter={epgFilter}
            youtubeCount={youtubeCount}
            twitchCount={twitchCount}
            kickCount={kickCount}
            epgCount={epgCount}
            onToggleYoutube={() => setYoutubeFilter(!youtubeFilter)}
            onToggleTwitch={() => setTwitchFilter(!twitchFilter)}
            onToggleKick={() => setKickFilter(!kickFilter)}
            onToggleEpg={() => setEpgFilter(!epgFilter)}
          />
          <nav className="header-nav">
            {showGuestChrome ? (
              <>
                <button
                  type="button"
                  className="livenow-login-btn"
                  onClick={() => goToLogin(navigate, query)}
                >
                  Log in
                </button>
                <button
                  type="button"
                  className="livenow-signup-btn"
                  onClick={() => goToSignup(navigate, query)}
                >
                  Sign up
                </button>
              </>
            ) : (
              <UserMenu />
            )}
          </nav>
        </div>
      </header>
      <main className="results-main">
        <div className="results-inner">
          <div className="results-content">
            {isMobile && !noFiltersSelected && galleryEvents.length > 0 && (
              <div
                className={`results-density${compact ? ' is-compact' : ' is-cards'}`}
                role="group"
                aria-label="Result density"
              >
                <span className="results-density-pill" aria-hidden="true" />
                <button
                  type="button"
                  className={`results-density-btn${compact ? ' is-active' : ''}`}
                  onClick={() => handleDensity(true)}
                  aria-pressed={compact}
                >
                  Compact
                </button>
                <button
                  type="button"
                  className={`results-density-btn${!compact ? ' is-active' : ''}`}
                  onClick={() => handleDensity(false)}
                  aria-pressed={!compact}
                >
                  Cards
                </button>
              </div>
            )}
            {entityId ? <EntityHub entityId={entityId} results={galleryEvents} hub={hub} /> : null}
            {quotaNotice ? (
              <div className="guest-quota-banner" role="status">
                <p>
                  You&apos;ve used today&apos;s guest searches.{' '}
                  <button
                    type="button"
                    className="guest-notify-strip-link"
                    onClick={() => goToSignup(navigate, query)}
                  >
                    Sign up
                  </button>
                  {' '}to keep searching. Last results are still below.
                </p>
              </div>
            ) : null}
            {noFiltersSelected ? (
              <div className="no-results-message">
                No results. Please select at least one filter above.
              </div>
            ) : hubReady && results.length === 0 && concertEvents.length === 0 && entityId ? (
              <p className="results-empty-broadcasts" role="status">
                No live broadcasts right now
              </p>
            ) : (
              <div key={listAnimKey} className="results-animate">
                <ResultsTopic
                  title={directTitle}
                  events={direct}
                  {...filterProps}
                />
                {relatedByEntity.map((group, index) => (
                  <ResultsTopic
                    key={group.relatedEntity}
                    title={relatedSectionLabel(group.events, interest)}
                    events={group.events}
                    quiet
                    staggerOffset={
                      direct.length
                      + relatedByEntity
                        .slice(0, index)
                        .reduce((count, item) => count + item.events.length, 0)
                    }
                    {...filterProps}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      {onBack && (
        <button
          className="results-back-fab"
          onClick={onBack}
          aria-label="Back to search"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ResultsGallery;
